/**
 * CQL2 spatial / WKT helpers for constructing geometry literals
 * used with CQL2 spatial operators (S_INTERSECTS, S_WITHIN, etc.)
 */

// ─── WKT constructors ───────────────────────────────────────────────────────

/** POINT(lng lat) */
export function wktPoint(lng: number, lat: number): string {
  return `POINT(${lng} ${lat})`;
}

/** LINESTRING(lng1 lat1, lng2 lat2, ...) */
export function wktLineString(coords: [number, number][]): string {
  return `LINESTRING(${coords.map(([lng, lat]) => `${lng} ${lat}`).join(', ')})`;
}

/** POLYGON((lng1 lat1, lng2 lat2, ... , lng1 lat1)) — ring must be closed */
export function wktPolygon(ring: [number, number][]): string {
  const coords = ring.map(([lng, lat]) => `${lng} ${lat}`).join(', ');
  return `POLYGON((${coords}))`;
}

/** Build a bounding-box polygon from [west, south, east, north] */
export function wktBbox(bbox: [number, number, number, number]): string {
  const [w, s, e, n] = bbox;
  return wktPolygon([[w, s], [e, s], [e, n], [w, n], [w, s]]);
}

/** Build a circular polygon approximation (N-sided) from center + radius */
export function wktCircle(
  centerLng: number,
  centerLat: number,
  radiusNm: number,
  segments = 32,
): string {
  const degPerNm = 1 / 60;
  const coords: [number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const angle = (2 * Math.PI * i) / segments;
    const dLat = radiusNm * degPerNm * Math.cos(angle);
    const dLng = (radiusNm * degPerNm * Math.sin(angle)) / Math.cos((centerLat * Math.PI) / 180);
    coords.push([centerLng + dLng, centerLat + dLat]);
  }
  return wktPolygon(coords);
}

// ─── DWITHIN helper ──────────────────────────────────────────────────────────
// Note: DWITHIN is not in CQL2 core, but many OGC servers support it as an extension.
// Falls back to S_INTERSECTS with a buffered circle polygon if needed.

/**
 * DWITHIN — features within a distance of a geometry.
 * Generates: S_INTERSECTS(geometry, <circle-polygon>)
 * since DWITHIN is an extension not universally supported.
 */
export function dwithin(
  property: string,
  centerLng: number,
  centerLat: number,
  radiusNm: number,
): string {
  const circle = wktCircle(centerLng, centerLat, radiusNm);
  return `S_INTERSECTS(${property}, ${circle})`;
}

/**
 * Features intersecting the current map viewport.
 * @param bbox [west, south, east, north]
 */
export function intersectsViewport(property: string, bbox: [number, number, number, number]): string {
  return `S_INTERSECTS(${property}, ${wktBbox(bbox)})`;
}

// ─── Temporal operators (CQL2 temporal) ──────────────────────────────────────

/**
 * T_INTERSECTS — features whose temporal extent intersects a given instant or interval.
 * Example: T_INTERSECTS(valid_time, TIMESTAMP('2026-04-06T12:00:00Z'))
 */
export function tIntersects(property: string, temporalLiteral: string): string {
  return `T_INTERSECTS(${property}, ${temporalLiteral})`;
}

/**
 * T_DURING — features whose temporal property falls during an interval.
 * Example: T_DURING(effective_date, INTERVAL('2026-01-01','2026-12-31'))
 */
export function tDuring(property: string, start: string, end: string): string {
  return `T_DURING(${property}, INTERVAL('${start}','${end}'))`;
}

/**
 * T_AFTER — features whose temporal property is after a given instant.
 */
export function tAfter(property: string, instant: string): string {
  return `T_AFTER(${property}, TIMESTAMP('${instant}'))`;
}

/**
 * T_BEFORE — features whose temporal property is before a given instant.
 */
export function tBefore(property: string, instant: string): string {
  return `T_BEFORE(${property}, TIMESTAMP('${instant}'))`;
}
